import React, { useCallback, useState, useEffect } from 'react';
import {
  AppCard,
  AppInput,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import Scrollbars from 'react-custom-scrollbars';
import axios from 'axios';
import styled from 'styled-components';
import { axiosPath } from '../../../../../../routing/helpers/router';
import GGCLPlantRow from './GGCLPlantRow';
import GGCLPlantSelectedBlock from './GGCLPlantSelectedBlock';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import colors from '../../../../../../constants/colors';
import Translator from '../../../../../components/helpers/Translator';
import _ from 'lodash';

export default function GGCLPlantListTab(props) {
  const { obligationID } = props;
  const dispatch = useDispatch();
  const { moduleBridgeHex, filtersHex } = useParams();
  const [selectedPlantIndex, setSelectedPlantIndex] = useState();
  const plantList = useSelector(
    (s) => s.currentPage?.plantListTab?.[obligationID]
  );
  const [nameFilter, setNameFilter] = useState('');
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex].permissions.ggcl
  );

  // > LOAD PLANT LIST TAB
  const loadPlantListTab = async () => {
    axios
      .post(
        axiosPath(
          "api_r_user_module_globalLegalCompliance_evaluation_tab_plantList"
        ),
        {
          obligationID,
          filtersHex,
          moduleBridgeHex,
        }
      )
      .then((r) =>
        dispatch(
          currentPageAction.change("plantListTab", { [obligationID]: r.data })
        )
      )
      .catch((e) => dispatch(errorHandler(e)));
  };
  // < LOAD PLANT LIST TAB

  useEffect(() => {
    loadPlantListTab();
  }, []);

  const handlePlant = useCallback(
    (plant, index) => {
      if (
        nameFilter === undefined ||
        nameFilter === '' ||
        plant.name.toLowerCase().includes(nameFilter.toLowerCase())
      ) {
        return (
          <React.Fragment key={plant.hex}>
            <GGCLPlantRow
              plantIndex={index}
              obligationID={obligationID}
              onSelectedPlant={() => setSelectedPlantIndex(index)}
            />
          </React.Fragment>
        );
      }

      return <React.Fragment key={plant.hex} />;
    },
    [nameFilter, obligationID]
  );

  return (
    <AppCard bodyClassName="p-0 mt-2">
      {_.isNumber(selectedPlantIndex) ? (
        <GGCLPlantSelectedBlock
          plantIndex={selectedPlantIndex}
          obligationID={obligationID}
          onUnselectPlant={setSelectedPlantIndex}
        />
      ) : (
        <>
          <AppInput
            icon={<AppRemixIcon icon="search" />}
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-globallegalCompliance"
                name="search.plants"
              />
            )}
            value={nameFilter}
            onChange={setNameFilter}
            isClearable
          />
          <Scrollbars
            style={{
              height: '540px',
            }}
          >
            {plantList ? (
              <PlantContainer>
                <PlantLeyend permissions={permissions}>
                  <span>
                    <Translator
                      file="module-globallegalCompliance"
                      name="plant"
                    />
                  </span>
                  <span>
                    <Translator
                      file="module-globallegalCompliance"
                      name="latest.evidence"
                    />
                  </span>
                  <span>
                    <Translator
                      file="module-globallegalCompliance"
                      name="planned.dates"
                    />
                  </span>
                  {(permissions?.probability ||
                    permissions?.severity ||
                    permissions?.risk) && (
                    <span>
                      <Translator
                        file="module-globallegalCompliance"
                        name="probability.severity"
                      />
                    </span>
                  )}

                  <span />
                </PlantLeyend>
                {plantList?.map((plant, index) => handlePlant(plant, index))}
              </PlantContainer>
            ) : (
              <AppNoInfo minHeight={400} />
            )}
          </Scrollbars>
        </>
      )}
    </AppCard>
  );
}

const PlantContainer = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PlantLeyend = styled.li`
  display: grid;
  grid-template-columns:
    1fr
    150px
    150px
    ${(props) =>
      (props.permissions?.probability ||
        props.permissions?.severity ||
        props.permissions?.risk) &&
      '140px'} //ProbSev block
    86px;
  padding-left: 13px;
  height: 40px;
  span {
    text-align: center;
    border-right: 1px solid ${colors.grey400};
    border-bottom: 1px solid ${colors.grey400};
    padding-left: 4px;
    font-size: 12px;
    font-weight: bold;
  }
`;
